import { isHallStageScorePresent } from "../hall_1/medalDisplayData";

export const HALL2_UNLOCKED_KEY = "hall2_unlocked_hotspots";
export const HALL2_COMPLETED_KEY = "hall2_completed_hotspots";

/**
 * Hall of Heritage hotspot order (left → right in hall2.svg)
 * ↔ DB columns stage7 … stage10
 */
export const HALL2_HOTSPOT_ORDER = [
  "restore_masterpiece",
  "restore_journal",
  "museum_archive",
  "remember_me_if_you_can",
];

export const HALL2_HOTSPOT_LABELS = {
  restore_masterpiece: "Restore Masterpiece",
  restore_journal: "Restore Journal",
  museum_archive: "Museum Archive",
  remember_me_if_you_can: "Remember Me If You Can",
};

export const HALL2_HOTSPOT_ROUTES = {
  restore_masterpiece: "/hall-2/stage7",
  restore_journal: "/hall-2/stage8",
  museum_archive: "/hall-2/stage9",
  remember_me_if_you_can: "/hall-2/stage10",
};

/** First DB stage number for this hall. */
export const HALL2_STAGE_START = 7;

/** Routes for Hall of Heritage stages 7–10. */
export const HALL2_STAGE_ROUTES = {
  7: "/hall-2/stage7",
  8: "/hall-2/stage8",
  9: "/hall-2/stage9",
  10: "/hall-2/stage10",
};

/**
 * If this stage already has a DB score, skip to the next incomplete stage
 * (or /complete when the museum is fully restored). Returns null to stay.
 */
export function getRedirectIfHall2StageDone(stage, stageNumber) {
  if (!isHallStageScorePresent(stage, stageNumber)) return null;
  for (let n = stageNumber + 1; n <= 10; n += 1) {
    if (!isHallStageScorePresent(stage, n)) {
      return HALL2_STAGE_ROUTES[n] || "/hall-2";
    }
  }
  return "/complete";
}

/** Hub redirect when Hall of Heritage is fully restored. */
export function getHall2HubRedirect(stage) {
  if (!stage) return null;
  if (isHallStageScorePresent(stage, 10)) return "/complete";
  return null;
}

/**
 * Only one hotspot is clickable: first empty score among stage7–10.
 * Requires Hall of Champions (`stage6`) complete first.
 * Session completed cache covers demo / brief DB lag after save.
 */
export function resolveActiveHall2Hotspot(stage) {
  const champsDone =
    isHallStageScorePresent(stage, 6) ||
    sessionStorage.getItem("hall1_complete") === "1";
  if (!champsDone) return null;

  const sessionCompleted = new Set(readHall2Completed());

  for (let i = 0; i < HALL2_HOTSPOT_ORDER.length; i += 1) {
    const id = HALL2_HOTSPOT_ORDER[i];
    const stageNumber = HALL2_STAGE_START + i;
    const done =
      isHallStageScorePresent(stage, stageNumber) || sessionCompleted.has(id);
    if (!done) return id;
  }
  return null;
}

export function readHall2Unlocked() {
  try {
    const raw = sessionStorage.getItem(HALL2_UNLOCKED_KEY);
    const parsed = raw ? JSON.parse(raw) : [HALL2_HOTSPOT_ORDER[0]];
    return Array.isArray(parsed) ? parsed : [HALL2_HOTSPOT_ORDER[0]];
  } catch {
    return [HALL2_HOTSPOT_ORDER[0]];
  }
}

export function unlockHall2Hotspot(id) {
  const set = new Set(readHall2Unlocked());
  set.add(id);
  sessionStorage.setItem(HALL2_UNLOCKED_KEY, JSON.stringify([...set]));
}

export function readHall2Completed() {
  try {
    const raw = sessionStorage.getItem(HALL2_COMPLETED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function markHall2HotspotCompleted(id) {
  const set = new Set(readHall2Completed());
  set.add(id);
  sessionStorage.setItem(HALL2_COMPLETED_KEY, JSON.stringify([...set]));
}

/** Mirror DB progress into sessionStorage (optional cache). */
export function hydrateHall2ProgressFromStage(stage) {
  if (!stage) return;

  const unlocked = new Set();
  const completed = new Set();

  if (!isHallStageScorePresent(stage, 6)) {
    sessionStorage.setItem(HALL2_UNLOCKED_KEY, JSON.stringify([]));
    sessionStorage.setItem(HALL2_COMPLETED_KEY, JSON.stringify([]));
    return;
  }

  unlocked.add(HALL2_HOTSPOT_ORDER[0]);

  HALL2_HOTSPOT_ORDER.forEach((id, index) => {
    const stageNumber = HALL2_STAGE_START + index;
    if (isHallStageScorePresent(stage, stageNumber)) {
      completed.add(id);
      const next = HALL2_HOTSPOT_ORDER[index + 1];
      if (next) unlocked.add(next);
    }
  });

  sessionStorage.setItem(HALL2_UNLOCKED_KEY, JSON.stringify([...unlocked]));
  sessionStorage.setItem(HALL2_COMPLETED_KEY, JSON.stringify([...completed]));
}
